import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import TableLayout from "../../../components/TableLayout";
import { Link, useLocation } from "react-router-dom";
import ConfirmationPop from "../../../components/Modals/ConfirmationPop";
import dataHandler from "../../../hooks/dataHandler";
import {
  HOTEL_CHANGE_STATUS,
  HOTELDELETE,
  HOTELLIST,
} from "../../../services/ApiCalls";
import CustomPagination from "../../../components/Common/CustomPagination";
import TableActions from "../../../components/Common/TableActions";
import moment from "moment";
import Filter from "../../../components/Common/Filter";
import { activeInactiveOptions, csvImportEnum } from "../../../utilities/const";
import { catchAsync, checkResponse, exportCsvHandler } from "../../../utilities/utilities";
import Toggle from "../../../components/Common/Toggle";
import ImportCsv from "../../../components/Common/ImportCsv";
import ButtonLoader from "../../../components/Common/ButtonLoader";
import CountryFilter from "../../../components/Common/CountryFilter";
import { useTranslation } from "react-i18next";

import HotelStats from "../../../components/Common/PropertyFeature";
import HotelAdminFilter from "../../../components/Common/HotelAdminFilter";

const ManageHotel = () => {
  const [exportLoader, setExportLoader] = useState(false);
  const [statusLoaderId, setStatusLoaderId] = useState("");
  const { t } = useTranslation();
  const location = useLocation();
  const {
    setBody,
    body,
    data,
    setData,
    statusChangeHandler,
    loader,
    deleteModel,
    setDeleteModel,
    paginationHandler,
    searchHandler,
    total,
    deleteHandler,
  } = dataHandler({
    api: HOTELLIST,
  });
  useEffect(() => {
    const queryPage = new URLSearchParams(location.search).get("page");

    if (queryPage) {
      setBody((p) => ({ ...p, page: Number(queryPage) }));
    }
  }, []);

  const handleHotelStatusChange = catchAsync(
    async (item) => {
      const hotelId = item?._id;
      if (!hotelId || statusLoaderId) return;

      const nextStatus =
        item?.status?.toLowerCase() === "active" ? "Inactive" : "Active";

      setStatusLoaderId(hotelId);
      const res = await HOTEL_CHANGE_STATUS({
        hotelId,
        status: nextStatus,
      });

      const success = checkResponse({ res, showSuccess: true });
      if (success) {
        setData((prev) => ({
          ...prev,
          hotelList: prev?.hotelList?.map((hotel) =>
            hotel?._id === hotelId ? { ...hotel, status: nextStatus } : hotel
          ),
        }));
      }
      setStatusLoaderId("");
    },
    setStatusLoaderId
  );

  const columns = [
    {
      head: t("manageHotel.columns.serialNumber"),
      accessor: "#",
      component: (item, key) => (
        <span className="fw-semibold text-muted">
          {body.limit * (body.page - 1) + key + 1}
        </span>
      ),
    },
    {
      head: t("manageHotel.columns.hotelName"),
      accessor: "hotelName",
      component: (item) => (
        <span
          className="fw-semibold text-dark text-truncate d-inline-block"
          style={{ maxWidth: "150px" }}
        >
          {item.hotelName || t("manageHotel.placeholders.notAvailable")}
        </span>
      ),
    },
    {
      head: t("manageHotel.columns.hotelId"),
      accessor: "hotelId",
      component: (item) => (
        <span className="text-secondary">
          {item.hotelId || t("manageHotel.placeholders.notAvailable")}
        </span>
      ),
    },
    {
      head: t("manageHotel.columns.hotelPriority"),
      accessor: "label",
      component: (item) => (
        <span>
          {item.pinStatus?.label || t("manageHotel.placeholders.notAvailable")}
        </span>
      ),
    },
    {
      head: t("manageHotel.columns.totalRooms"),
      accessor: "totalRoom",
      component: (item) => (
        <span className="fw-semibold d-flex justify-content-center align-items-center">
          {item?.totalRoom || t("manageHotel.placeholders.notAvailable")}
        </span>
      ),
    },

    {
      head: t("manageHotel.columns.adminName"),
      accessor: "name",
      component: (item) => (
        <span
          className="fw-semibold text-capitalize text-truncate d-inline-block"
          style={{ maxWidth: "150px" }}
        >
          {item.userDetail?.name || t("manageHotel.placeholders.notAvailable")}
        </span>
      ),
    },
    {
      head: t("manageHotel.columns.mobileNumber"),
      accessor: "mobileNumber",
      component: (item) => (
        <span className="text-nowrap">
          {item?.countryCode
            ? `+${item.countryCode} ${item?.mobileNumber}`
            : item?.mobileNumber || t("manageHotel.placeholders.notAvailable")}
        </span>
      ),
    },
    {
      head: t("manageHotel.columns.email"),
      accessor: "email",
      component: (item) => (
        <span
          className="d-inline-block text-truncate"
          title={item.email}
          style={{
            maxWidth: "200px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            verticalAlign: "middle",
          }}
        >
          {item.email || t("manageHotel.placeholders.notAvailable")}
        </span>
      ),
    },
    {
      head: t("manageHotel.columns.location"),
      accessor: "address",
      component: (item) => {
        const words = item?.address?.split(" ");
        const truncatedText = words?.slice(0, 4).join(" ");
        const displayText =
          words?.length > 4
            ? `${truncatedText}...`
            : truncatedText || t("manageHotel.placeholders.notAvailable");
        return (
          <span
            className="text-secondary text-truncate d-inline-block"
            title={item?.address}
            style={{ maxWidth: "150px" }}
          >
            {displayText}
          </span>
        );
      },
    },
    {
      head: t("manageHotel.columns.createdAt"),
      accessor: "createdAt",
      component: (item) => (
        <span style={{ color: "red", fontWeight: 600 }}>
          {item.createdAt
            ? moment(item.createdAt).format("DD-MM-YYYY, HH:mm")
            : t("manageHotel.placeholders.notAvailable")}
        </span>
      ),
    },
    {
      head: "Approved",
      accessor: "isApproved",
      component: (item) => (
        <span>
          {item.isApproved ? "Yes" : "No"}
        </span>
      ),
    },
    {
      head: t("manageHotel.columns.status"),
      accessor: "status",
      title: t("manageHotel.statusTooltip"),
      component: (item) => {
        const isActive = item?.status?.toLowerCase() === "active";
        const isUpdating = statusLoaderId === item?._id;

        return (
          <span className="d-flex align-items-center gap-2">
            <Toggle
              isChecked={isActive}
              onChange={() => handleHotelStatusChange(item)}
            />
            <span
              className={`fw-bold ${isActive ? "text-success" : "text-danger"}`}
            >
              {isUpdating
                ? t("common.updating", "Updating...")
                : item.status || t("manageHotel.placeholders.notAvailable")}
            </span>
          </span>
        );
      },
    },
    {
      head: t("manageHotel.columns.action"),
      accessor: "action",
      component: (item) => (
        <TableActions
          viewLink={`/hotel/detail/${item._id}?page=${body.page}`}
          editUrl={`/hotel/edit/${item._id}?page=${body.page}`}
          setDeleteModel={() =>
            setDeleteModel({ show: true, dumpId: item._id })
          }
        />
      ),
    },
  ];

  return (
    <>
      <ConfirmationPop
        type={"delete"}
        confirmation={deleteModel.show}
        setConfirmation={() =>
          setDeleteModel({
            show: false,
            dumpId: "",
          })
        }
        confirmHandler={() =>
          deleteHandler(() => HOTELDELETE({ _id: deleteModel.dumpId }))
        }
      />
      <ConfirmationPop
        confirmation={deleteModel.show}
        setConfirmation={() => setDeleteModel({ show: false, dumpId: "" })}
        type={"delete"}
        confirmHandler={() =>
          deleteHandler(() => HOTELDELETE({ _id: deleteModel.dumpId }))
        }
      />
      <section className="manageServiceCategory position-relative">
        <Container>
          <Row>
            <Col lg="12 ">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0 py-2 fw-bold text-dark text-capitalize">
                  {t("manageHotel.title")}
                </h4>
                <div className="right">
                  <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center gap-10 flex-wrap">
                    <li className="">
                      <ImportCsv
                        item={csvImportEnum.importHotel}
                        callBack={() => refetch()}
                        type="hotel"
                      />
                    </li>
                    <li className="">
                      <Button
                        className="d-flex align-items-center justify-content-center fw-sbold commonBtn GreyBtn"
                        style={{ height: 40, minWidth: 100, fontSize: 12 }}
                        type="button"
                        onClick={() =>
                          exportCsvHandler("hotel", setExportLoader)
                        }
                      >
                        {exportLoader ? (
                          <ButtonLoader />
                        ) : (
                          t("manageHotel.buttons.exportCsv")
                        )}
                      </Button>
                    </li>
                    <li className="">
                      <Link
                        to="/hotel/add"
                        className="d-flex btn btn-primary align-items-center justify-content-center fw-sbold commonBtn "
                        style={{ height: 40, minWidth: 100, fontSize: 12 }}
                      >
                        {t("manageHotel.buttons.addNewHotel")}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              <HotelStats platformData={data?.platform} />
            </Col>

            <Col lg="12" className="my-2">
              <div className="tableFilter d-flex align-items-center justify-content-between flex-wrap gap-10 mb-3">
                <div className="left">
                  <HotelAdminFilter
                    body={body}
                    searchHandler={searchHandler}
                    setBody={setBody}
                    statusFilterOptionArr={activeInactiveOptions}
                  />
                </div>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              <TableLayout
                column={columns}
                data={data?.hotelList}
                loader={loader}
              />
              <CustomPagination
                total={total}
                pageChangeHandler={paginationHandler}
                body={body}
                setBody={setBody}
              />
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default ManageHotel;
